import {
  computeEdgePositions,
  entityCodesToText,
  getTransformAttr,
} from "../utils.js";
import { Position } from "../interfaces.js";

export interface Mindmap {
  type: "mindmap";
  nodes: MindmapNode[];
  edges: MindmapEdge[];
}

export interface MindmapNode {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
  branchIndex: number;
  parentId?: string; // Track parent for edge binding
  type?: string;     // Track node type (e.g., circle/rect)
  containerStyle: {
    fill?: string;
    stroke?: string;
  };
}

export interface MindmapEdge {
  fromId: string;
  toId: string;
}

const getElementPosition = (el: Element, containerEl: Element): Position => {
  const { transformX, transformY } = getTransformAttr(el);
  const position = { x: transformX, y: transformY };
  
  let parent = el.parentElement;
  while (parent && parent !== containerEl) {
    const { transformX: px, transformY: py } = getTransformAttr(parent);
    position.x += px;
    position.y += py;
    parent = parent.parentElement;
  }
  return position;
};

export const parseMermaidMindmapDiagram = (
  db: any,
  containerEl: Element
): Mindmap => {
  const nodes: MindmapNode[] = [];
  const edges: MindmapEdge[] = [];

  const tree = db.getMindmap();
  const svgNodes = Array.from(containerEl.querySelectorAll(".node, .mindmap-node"));

  let nodeCounter = 0;
  const walk = (node: any, depth: number, branchIndex: number, parentId?: string) => {
    const svgEl = svgNodes[nodeCounter] as SVGGraphicsElement;
    const currentId = node.id || `node-${nodeCounter}`;
    
    if (svgEl) {
      const position = getElementPosition(svgEl, containerEl);
      const bbox = svgEl.getBBox();
      
      // Check if it's a circular node (root often is)
      const isCircle = svgEl.querySelector("circle") !== null;

      // Extract color and text directly from SVG for maximum accuracy
      const rect = svgEl.querySelector("rect, circle, polygon, path") || svgEl;
      const computedStyle = window.getComputedStyle(rect);
      const fill = computedStyle.fill || rect.getAttribute("fill") || "";
      const stroke = computedStyle.stroke || rect.getAttribute("stroke") || "";
      
      // Mermaid often puts text in a .label or directly in the element
      const textEl = svgEl.querySelector(".label, text");
      const text = textEl?.textContent || entityCodesToText(node.text || "");

      nodes.push({
        id: currentId,
        text: text.trim(),
        x: position.x + bbox.x,
        y: position.y + bbox.y,
        width: bbox.width,
        height: bbox.height,
        depth,
        branchIndex,
        parentId,
        type: isCircle ? "ellipse" : "rectangle",
        containerStyle: {
          fill: fill === "none" ? "" : fill,
          stroke: stroke === "none" ? "" : stroke,
        },
      });

      if (parentId) {
        edges.push({
          fromId: parentId,
          toId: currentId,
        });
      }
    }
    
    nodeCounter++;
    
    if (node.children) {
      node.children.forEach((child: any, index: number) => {
        const nextBranchIndex = depth === 0 ? index : branchIndex;
        walk(child, depth + 1, nextBranchIndex, currentId);
      });
    }
  };

  if (tree) {
    walk(tree, 0, -1);
  }

  return {
    type: "mindmap",
    nodes,
    edges,
  };
};
